import { useGetFile } from '@/hooks';

// Matches sources that are already directly usable in an <img src>: data URIs,
// blob URLs, and absolute http(s) URLs. Anything else is treated as a storage
// key and resolved to a blob URL via useGetFile.
const DIRECT_SRC_RE = /^(?:https?:|blob:|data:)/i;

/** Resolution state for a proof image source. `src` is the displayable URL, or
 *  `undefined` while loading, on error, or when there is no source at all. */
export interface ResolvedImageSrc {
  src: string | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Resolves a proof image source to a displayable URL plus loading/error state.
 *
 * - data URIs / blob URLs / absolute http(s) URLs resolve synchronously and
 *   unchanged (byte-identical to the input) → `{ src, isLoading: false,
 *   isError: false }`, so legacy proof processors render exactly as before with
 *   no new loading gap.
 * - Any other value is treated as a storage key (e.g. an agent screenshot's S3
 *   key such as `screenshots/x.png`) and fetched through `useGetFile`:
 *   `isLoading` while the query is fetching; `isError` if it errors or resolves
 *   to an empty string (`useGetFile` returns `''` for a missing/failed file, so
 *   an empty result is a load failure, not "still loading"); otherwise `src` is
 *   the resolved blob URL.
 */
export function useResolvedImageSrc(
  source: string | undefined
): ResolvedImageSrc {
  const isDirect = !!source && DIRECT_SRC_RE.test(source);
  const storageKey = source && !isDirect ? source : undefined;

  const {
    data: blobUrl,
    isLoading: fileLoading,
    isError: fileError,
  } = useGetFile<string>({ name: storageKey ?? '' }, { enabled: !!storageKey });

  // No source at all → nothing to render (not loading, not an error).
  if (!source) return { src: undefined, isLoading: false, isError: false };

  // Direct source usable as-is, synchronously (preserves the working path).
  if (isDirect) return { src: source, isLoading: false, isError: false };

  // Storage-key path, driven by the useGetFile query status.
  if (fileError) return { src: undefined, isLoading: false, isError: true };
  if (fileLoading) return { src: undefined, isLoading: true, isError: false };
  if (!blobUrl) return { src: undefined, isLoading: false, isError: true };
  return { src: blobUrl, isLoading: false, isError: false };
}
